/* eslint-disable no-console */
import { Dialog, Transition } from '@headlessui/react';
import { Fragment, useContext, useState, useEffect } from 'react';
import { analytics } from "../../helpers/analytics";

import { RatingIcon } from '../../Icons';
import { ToolsListItem } from './ToolsListItem';
import { Card } from '../Card';

import { UserContext } from '../../context/userContext';
import { PaywallModal } from '../PaywallModal';
import { IBrushFields } from '../../@types/generated/contentful';
import { SignIn } from '../SignIn';
import { getContentfulImageSrcSet, getContentfulImageUrl } from '../../helpers/image';

type Props = {
  set: IBrushFields;
  isOpen: boolean;
  isBrushset?: boolean;
  onClose: () => void;
};

const ToolsModal = ({ set, isOpen, isBrushset, onClose }: Props) => {
  const [isPaywallOpen, setPaywallOpen] = useState(false);
  const [isAuthOpen, setAuthOpen] = useState(false);
  const [rating, setRating] = useState<null | string>();

  const { image, color, section: brushes, title, description } = set;
  const heroImage = getContentfulImageUrl(image, { width: 760 });
  const heroSrcSet = getContentfulImageSrcSet(image, [420, 760, 1100]);

  const { user } = useContext(UserContext);
  const { hasPremium = false, plan = null } = user || {};

  const handleDownloadButtonClick = () => {
    if (user && user.email) {
      setPaywallOpen(true);
      analytics.track('Paywall', {
        premium: false,
        trial: false,
        source: "tool_download",
        set: title,
      });
    } else {
      analytics.track("Auth Required", {
        source: "tool_download",
        set: title,
      });
      setAuthOpen(true);
    }
  };

  const handleRatingClick = (value: number) => {
    if (rating) {
      return;
    }
    localStorage.setItem(`rating_${title}`, value.toString());
    setRating(value.toString());
    analytics.track('Ratting tapped', { set: title, value });
    analytics.track("Tool Rated", { set: title, value });
  };

  const handleAuthClose = () => {
    setAuthOpen(false);
  };

  const handleAuthSuccess = () => {
    setAuthOpen(false);
    analytics.track("Auth Gate Completed", {
      source: "tool_download",
      set: title,
    });
    setPaywallOpen(true);
  };

  useEffect(() => {
    if (isOpen) {
      analytics.track("Tool Modal Viewed", {
        set: title,
        has_brushes: Boolean(brushes?.length),
        brush_count: brushes?.length,
      });
    }
  }, [brushes?.length, isOpen, title]);

  useEffect(() => {
    const currentRating = localStorage.getItem(`rating_${title}`);
    setRating(currentRating);
  }, [setRating, title]);

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as='div' className='fixed z-10 inset-0 overflow-y-auto' onClose={onClose}>
        <div className='flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0'>
          <Transition.Child
            as={Fragment}
            enter='ease-out duration-300'
            enterFrom='opacity-0'
            enterTo='opacity-100'
            leave='ease-in duration-200'
            leaveFrom='opacity-100'
            leaveTo='opacity-0'
          >
            <Dialog.Overlay className='fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity' />
          </Transition.Child>

          <span className='hidden sm:inline-block sm:align-middle sm:h-screen' aria-hidden='true'>
            &#8203;
          </span>
          <Transition.Child
            as={Fragment}
            enter='ease-out duration-300'
            enterFrom='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
            enterTo='opacity-100 translate-y-0 sm:scale-100'
            leave='ease-in duration-200'
            leaveFrom='opacity-100 translate-y-0 sm:scale-100'
            leaveTo='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
          >
            <div
              className={`
          relative 
        inline-block 
        overflow-hidden 
        transform 
        transition-all s
        align-middle 
        w-[683px]
        md:w-full
        `}
            >
              <Card theme='light' size='large' onClose={onClose}>
                <div>
                  <div
                    className={`h-[380px] 
                relative 
                overflow-hidden
                rounded-t-[22px] md:rounded-none flex items-center justify-center`}
                    style={{ backgroundColor: color }}
                  >
                    {heroImage && (
                      <img
                        src={heroImage}
                        srcSet={heroSrcSet}
                        sizes='(max-width: 768px) 100vw, 683px'
                        alt=''
                        loading='eager'
                        decoding='async'
                        className='max-w-[100%] max-h-[80%] select-none'
                      />
                    )}
                    <div
                      className='p-6 md:p-4 absolute flex bottom-0 justify-between w-full text-white'
                      style={{
                        backgroundImage:
                          'linear-gradient(to top, rgba(0, 0, 0, 0.88) 0%, rgba(0, 0, 0, 0.58) 56%, rgba(0, 0, 0, 0) 100%)',
                        textShadow: '0 2px 10px rgba(0, 0, 0, 0.7)',
                      }}
                    >
                      <div className='text-left max-h-full'>
                        <h1 className='text-3xl leading-3xl font-bold'>
                          {title} brushes for Procreate
                        </h1>
                        {isBrushset && brushes && (
                          <p className='text-lg leading-lg font-medium text-white/90'>
                            {brushes.length} {brushes.length > 1 ? 'brushes' : 'brush'}
                          </p>
                        )}
                        <p className='text-lg leading-lg xs:text-sm xs-text-sm text-white/80'>
                          {description}
                        </p>
                      </div>
                      <div className='max-h-min whitespace-nowrap self-end'>
                        <div className='text-white/50'>
                          <RatingIcon
                            className={`w-[27px] ${rating ? '' : 'cursor-pointer'} xs:w-[20px] inline-block`}
                            onClick={() => handleRatingClick(1)}
                            style={{ color: Number(rating) >= 1 ? '#ffffff' : '' }}
                          />
                          <RatingIcon
                            className={`w-[27px] ml-1 ${rating ? '' : 'cursor-pointer'} xs:w-[20px] inline-block`}
                            onClick={() => handleRatingClick(2)}
                            style={{ color: Number(rating) >= 2 ? '#ffffff' : '' }}
                          />
                          <RatingIcon
                            className={`w-[27px] ml-1 ${rating ? '' : 'cursor-pointer'} xs:w-[20px] inline-block`}
                            onClick={() => handleRatingClick(3)}
                            style={{ color: Number(rating) >= 3 ? '#ffffff' : '' }}
                          />
                          <RatingIcon
                            className={`w-[27px] ml-1 ${rating ? '' : 'cursor-pointer'} xs:w-[20px] inline-block`}
                            onClick={() => handleRatingClick(4)}
                            style={{ color: Number(rating) >= 4 ? '#ffffff' : '' }}
                          />
                          <RatingIcon
                            className={`w-[27px] ml-1 ${rating ? '' : 'cursor-pointer'} xs:w-[20px] inline-block`}
                            onClick={() => handleRatingClick(5)}
                            style={{ color: Number(rating) >= 5 ? '#ffffff' : '' }}
                          />
                        </div>
                        <p className='text-lg leading-lg text-white font-medium text-right'>
                          {rating ? 'Thank you!' : 'Tap to rate'}
                        </p>
                      </div>
                    </div>
                  </div>
                  {brushes && (
                    <ul className='pt-4 pb-8'>
                      {brushes.map((item, i: number) => (
                        <li key={item.fields.title}>
                          <ToolsListItem
                            setTitle={title}
                            brush={item}
                            isLastItem={i === brushes.length - 1}
                            image={image}
                            onDownload={handleDownloadButtonClick}
                          />
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </Card>
            </div>
          </Transition.Child>
        </div>
        <SignIn isOpen={isAuthOpen} onClose={handleAuthClose} onSuccess={handleAuthSuccess} />
        <PaywallModal
          isOpen={isPaywallOpen}
          onClose={() => setPaywallOpen(false)}
          userId={(user && user.id) || null}
          email={(user && user.email) || null}
          hasPremium={hasPremium}
          isTrial={(plan && plan.isTrial) || false}
        />
      </Dialog>
    </Transition.Root>
  );
};

export { ToolsModal };
